import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Mail,
  ShieldCheck,
} from "lucide-react";

import { person } from "../data/profile";
import { cn } from "../lib/utils";

/**
 * LeadForm — the compact enquiry card that sits in the hero of every
 * landing page.
 *
 * Placement is the whole point. Search traffic arrives having already
 * stated intent in the query; making a recruiter read 2,000 words before
 * they find a way to contact you loses the ones who were ready in the
 * first ten seconds. The long-form content still exists below for the
 * ones who want it — this just stops the page gating the conversion.
 *
 * Three fields, because every additional field measurably costs
 * completions and none of the others tell me anything a reply won't.
 */
export default function LeadForm({ source = "Landing hero", role }) {
  const [form, setForm] = useState({ name: "", email: "", message: "", hp: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", text: "All three fields are needed." });
      return;
    }
    if (form.hp) return;

    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch("/api/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
          source,
        }),
      });
      if (res.ok) {
        setStatus({ type: "success", text: "Sent. You'll hear back within a working day." });
        setForm({ name: "", email: "", message: "", hp: "" });
      } else {
        setStatus({ type: "error", text: "Server declined — email works as a fallback." });
      }
    } catch {
      setStatus({ type: "error", text: "Network error — email works as a fallback." });
    } finally {
      setBusy(false);
      window.setTimeout(() => setStatus(null), 7000);
    }
  };

  const field =
    "w-full rounded-xl border border-white/[0.09] bg-ink-950/60 px-3.5 py-2.5 " +
    "text-[0.85rem] text-slate-100 placeholder-slate-600 outline-none " +
    "transition-colors duration-300 focus:border-brand-400/60 focus:bg-ink-950/90";

  return (
    <div className="panel overflow-hidden rounded-3xl">
      {/* Header strip — availability is the first thing a recruiter checks */}
      <div className="border-b border-white/[0.07] bg-gradient-to-br from-brand-500/[0.09] to-transparent px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <p className="font-display text-[0.95rem] font-semibold text-slate-50">
            {role ? `Hiring ${role}?` : "Start a conversation"}
          </p>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-brand-400/30 bg-brand-500/10 px-2 py-1 font-mono text-[0.5rem] uppercase tracking-label text-brand-300">
            <span className="h-1 w-1 animate-pulse rounded-full bg-brand-400" />
            Available
          </span>
        </div>
        <p className="mt-1.5 text-[0.74rem] leading-relaxed text-slate-400">
          Three fields. Straight to my inbox, answered within a working day.
        </p>
      </div>

      <form onSubmit={onSubmit} noValidate className="space-y-2.5 p-5">
        <input
          name="name"
          value={form.name}
          onChange={onChange}
          autoComplete="name"
          placeholder="Your name"
          aria-label="Your name"
          className={field}
        />
        <input
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          autoComplete="email"
          placeholder="Work email"
          aria-label="Work email"
          className={field}
        />
        <textarea
          name="message"
          rows={3}
          value={form.message}
          onChange={onChange}
          placeholder="Role, product, or what's currently in the way…"
          aria-label="Your message"
          className={cn(field, "resize-y")}
        />

        <div className="sr-only-seo" aria-hidden="true">
          <label>
            Leave empty
            <input
              name="hp"
              value={form.hp}
              onChange={onChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full py-2.5 text-[0.85rem] disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Sending
            </>
          ) : (
            <>
              <Send className="h-3.5 w-3.5" />
              Send enquiry
            </>
          )}
        </button>

        <AnimatePresence>
          {status && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              role="status"
              className={cn(
                "flex items-start gap-2 rounded-xl border px-3 py-2 text-[0.74rem]",
                status.type === "success"
                  ? "border-brand-400/30 bg-brand-500/[0.08] text-brand-200"
                  : "border-rose-400/30 bg-rose-500/[0.08] text-rose-200"
              )}
            >
              {status.type === "success" ? (
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              ) : (
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              )}
              {status.text}
            </motion.p>
          )}
        </AnimatePresence>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-white/[0.06] pt-3">
          <a
            href={`mailto:${person.email}`}
            className="inline-flex items-center gap-1.5 text-[0.72rem] text-slate-400 transition-colors hover:text-brand-300"
          >
            <Mail className="h-3 w-3" />
            {person.email}
          </a>
          <span className="inline-flex items-center gap-1.5 text-[0.7rem] text-slate-600">
            <ShieldCheck className="h-3 w-3" />
            No list, no newsletter
          </span>
        </div>
      </form>
    </div>
  );
}
